import { type CSSProperties } from 'react'
import pt from '@/shared/styles/policyTabs.module.css'
import type { ProviderArtefactVersion } from '@/lib/apiTypes'
import { formatDate, advisoryUrl } from '@/lib/format'
import { VersionRefsPanel } from '@/features/providers/tabs/version/RefsTab'
import { VersionUsagePanel } from '@/features/providers/tabs/version/UsageTab'
import { VersionFindingsSubtable } from '@/features/providers/tabs/version/VulnsTab'

// ── Version-row expansion: tabbed drill-down ──────────────────────────────────
export type VersionExpansionTab = 'vulns' | 'refs' | 'usage'

/**
 * The expanded row's tabbed panel: Vulnerabilities (findings + filters, with the patched build's
 * fix status per CVE when one exists), Module references
 * (required_providers declarations admitting this version) and Live usage (state workspaces
 * with this exact version locked). Replaces the separate used-by / referenced-by modals -
 * one uncluttered surface under the row. Underline tabs reuse the PoliciesOverview idiom,
 * sized down for the dense table context.
 */
export function VersionExpansion({ orgId, upstream, namespace, type, v, tab, onTab }: {
  orgId: string
  upstream: string
  namespace: string
  type: string
  v: ProviderArtefactVersion
  tab: VersionExpansionTab
  onTab: (t: VersionExpansionTab) => void
}) {
  const tabs: Array<{ key: VersionExpansionTab; label: string; count: number }> = [
    { key: 'vulns', label: 'Vulnerabilities', count: v.findings.length },
    { key: 'refs', label: 'Module references', count: v.referenced_module_count },
    { key: 'usage', label: 'Live usage', count: v.usage_workspace_count },
  ]
  return (
    <div>
      <div className={pt.tabBar} role="tablist" aria-label={`v${v.version} details`} style={{ marginBottom: 12 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`${pt.tab} ${tab === t.key ? pt.tabActive : ''}`}
            style={{ fontSize: '0.75rem', padding: '5px 12px' }}
            onClick={() => onTab(t.key)}
          >
            {t.label} <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.68rem' }}>({t.count})</span>
          </button>
        ))}
      </div>
      {tab === 'vulns' && <VersionFindingsSubtable orgId={orgId} upstream={upstream} namespace={namespace} type={type} v={v} />}
      {tab === 'refs' && <VersionRefsPanel orgId={orgId} refs={v.referenced_by_modules} />}
      {tab === 'usage' && <VersionUsagePanel orgId={orgId} usedBy={v.used_by} />}
    </div>
  )
}


/** A vulnerability id as a link to its external advisory page (NVD / GitHub Advisories / OSV). */
export function CveLink({ id, style }: { id: string; style?: CSSProperties }) {
  const href = advisoryUrl(id)
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={style}>{id}</a>
    : <span style={style}>{id}</span>
}

/** Collapsed-row badge tooltip: the revision, when it shipped, and what it fixed - which for a
 *  version with nothing fixable is nothing, and the build is the provenance alone. */
export function buildBadgeTip(p: NonNullable<ProviderArtefactVersion['build']>, base: string): string {
  const when = p.published_at != null ? `, published ${formatDate(p.published_at)}` : ''
  const advisory = p.report_only_count > 0 ? `; ${p.report_only_count} not auto-fixable (advisory)` : ''
  if (p.fixed_count === 0) {
    return `${p.full_version} (revision ${p.patch_rev}${when}) is terramantle's own build of v${base} from source - pinned, gate-passed, signed and scanned. No CVE fixes were needed${advisory}. Expand the row for details.`
  }
  const cves = p.manifest.map((m) => m.cve_id)
  const shown = cves.slice(0, 3).join(', ')
  const more = cves.length > 3 ? ` +${cves.length - 3} more` : ''
  return `${p.full_version} (revision ${p.patch_rev}${when}) fixes ${p.fixed_count} CVE${p.fixed_count === 1 ? '' : 's'}${shown ? `: ${shown}${more}` : ''}${advisory}. Expand the row for details.`
}
